import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seo";
import { WHATSAPP_HREF } from "@/lib/links";
import { trackWhatsAppClick } from "@/lib/analytics";

const ease = [0.22, 1, 0.36, 1];

export default function NotFound() {
    return (
        <>
            <Seo {...SEO.notFound} />
            <section
                data-testid="notfound-page"
                className="relative min-h-[80vh] flex items-center pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden"
            >
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10 w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease }}
                        className="flex flex-wrap items-center justify-between gap-4 mb-12 lg:mb-16 text-xs uppercase tracking-[0.22em] font-bold text-black/60"
                    >
                        <span>/ Error · 404</span>
                        <span>Mumbai · India ⇄ Worldwide</span>
                    </motion.div>

                    <h1
                        data-testid="notfound-heading"
                        className="font-display font-black uppercase leading-[0.86] tracking-[-0.04em] text-[20vw] sm:text-[16vw] lg:text-[13vw]"
                    >
                        <motion.span
                            initial={{ opacity: 0, y: 60 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, ease, delay: 0.05 }}
                            className="block outline-text"
                        >
                            404
                            <span className="text-olive">·</span>
                        </motion.span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease, delay: 0.3 }}
                        className="mt-8 font-serif italic text-2xl sm:text-3xl lg:text-4xl text-black/75 max-w-2xl"
                    >
                        This seed hasn't been planted here.
                    </motion.p>

                    <motion.p
                        data-testid="notfound-copy"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease, delay: 0.4 }}
                        className="mt-6 max-w-xl text-base lg:text-lg text-black/70 leading-relaxed"
                    >
                        The page you're looking for doesn't exist or may have
                        moved. Let's get you back to solid ground.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease, delay: 0.55 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <Link
                            data-testid="notfound-home-link"
                            to="/"
                            className="group inline-flex items-center gap-3 bg-obsidian text-bone px-7 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-olive hover:text-obsidian transition-colors duration-300"
                        >
                            <span
                                aria-hidden
                                className="inline-block w-2 h-2 bg-olive-mint rounded-full"
                            />
                            Back to Home
                            <span
                                aria-hidden
                                className="transition-transform duration-300 group-hover:translate-x-1"
                            >
                                →
                            </span>
                        </Link>
                        <a
                            data-testid="notfound-whatsapp-link"
                            href={WHATSAPP_HREF}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() =>
                                trackWhatsAppClick({
                                    section: "notfound",
                                    buttonName: "notfound_whatsapp",
                                })
                            }
                            className="inline-flex items-center gap-2 px-2 py-4 text-xs uppercase tracking-[0.2em] font-bold link-underline"
                        >
                            Chat on WhatsApp
                        </a>
                    </motion.div>
                </div>
            </section>
        </>
    );
}
